/* global axios */
import ApiClient from './ApiClient';

// Helper function to build search parameters for tickets
export const buildTicketSearchParams = (query, page = 1, sort = 'created') => {
  let params = `q=${encodeURIComponent(query)}&page=${page}`;
  if (sort) {
    params = `${params}&sort=${sort}`;
  }
  // Exclude priority and status fields from search
  params = `${params}&exclude_fields=priority,status`;
  // Enable alphanumeric search across all searchable columns
  params = `${params}&search_fields=folio,title,assignee,created,sla`;
  return params;
};

class TicketsAPI extends ApiClient {
  constructor() {
    super('tickets', { accountScoped: true });
  }

  get(params = {}) {
    return axios.get(this.url, { params });
  }

  show(id) {
    return axios.get(`${this.url}/${id}`);
  }

  create(ticketData) {
    return axios.post(this.url, { ticket: ticketData });
  }

  update(id, ticketData) {
    return axios.patch(`${this.url}/${id}`, { ticket: ticketData });
  }

  delete(id) {
    return axios.delete(`${this.url}/${id}`);
  }

  // Issues (idempotent) the public document-request share token for the ticket.
  generateDocumentRequestShare(id) {
    return axios.post(`${this.url}/${id}/document_request_share`);
  }

  // Revokes the public document-request share token for the ticket.
  revokeDocumentRequestShare(id) {
    return axios.delete(`${this.url}/${id}/document_request_share`);
  }

  meta() {
    return axios.get(`${this.url}/meta`);
  }

  getFilterOptions(params = {}) {
    return axios.get(`${this.url}/filter_options`, { params });
  }

  dashboard(params = {}) {
    return axios.get(`${this.url}/dashboard`, { params });
  }

  reportStats(params = {}) {
    return axios.get(`${this.url}/report_stats`, { params });
  }

  search(query, page = 1, sort = 'created') {
    // Enhanced search with alphanumeric support and field exclusion
    const requestURL = `${this.url}/search?${buildTicketSearchParams(query, page, sort)}`;
    return axios.get(requestURL);
  }

  // Advanced search with custom parameters
  advancedSearch(params = {}) {
    const {
      query = '',
      page = 1,
      sort = 'created',
      searchFields = ['folio', 'title', 'assignee', 'created', 'sla'],
      excludeFields = ['priority', 'status'],
    } = params;

    const searchParams = {
      q: query,
      page,
      sort,
      search_fields: searchFields.join(','),
      exclude_fields: excludeFields.join(','),
      search_type: 'alphanumeric',
    };

    return axios.get(`${this.url}/search`, { params: searchParams });
  }

  filter(filters) {
    return axios.post(`${this.url}/filter`, filters);
  }

  assign(id, assigneeId) {
    return axios.post(`${this.url}/${id}/assign`, { assignee_id: assigneeId });
  }

  escalate(id, escalateToId, notes = null) {
    return axios.post(`${this.url}/${id}/escalate`, {
      escalate_to_id: escalateToId,
      notes,
    });
  }

  resolve(id, resolutionNotes = null) {
    return axios.post(`${this.url}/${id}/resolve`, {
      resolution_notes: resolutionNotes,
    });
  }

  close(id) {
    return axios.post(`${this.url}/${id}/close`);
  }

  reopen(id) {
    return axios.post(`${this.url}/${id}/reopen`);
  }

  verify(id) {
    return axios.post(`${this.url}/${id}/verify`);
  }

  rejectVerification(id, notes = null) {
    return axios.post(`${this.url}/${id}/reject_verification`, { notes });
  }

  createFollowUp(id, params = {}) {
    return axios.post(`${this.url}/${id}/create_follow_up`, { ticket: params });
  }

  getConversationMessages(ticketId, params = {}) {
    return axios.get(`${this.url}/${ticketId}/conversation_messages`, {
      params,
    });
  }

  // Ticket conversations (internal comments)
  getConversations(ticketId) {
    return axios.get(`${this.url}/${ticketId}/conversations`);
  }

  createConversation(
    ticketId,
    message,
    isPrivate = false,
    clientVisible = false
  ) {
    return axios.post(`${this.url}/${ticketId}/conversations`, {
      ticket_conversation: {
        message,
        private: isPrivate,
        client_visible: clientVisible,
      },
    });
  }

  toggleTypingStatus(ticketId, status, isPrivate = false) {
    return axios.post(
      `${this.url}/${ticketId}/conversations/toggle_typing_status`,
      {
        typing_status: status,
        is_private: isPrivate,
      }
    );
  }

  createServiceOrder(ticketId, orderData) {
    return axios.post(`${this.url}/${ticketId}/create_service_order`, {
      service_order: orderData,
    });
  }

  // Ticket attachments
  getConversationAttachments(ticketId) {
    return axios.get(`${this.url}/${ticketId}/conversation_attachments`);
  }

  linkAttachment(ticketId, attachmentId) {
    return axios.post(`${this.url}/${ticketId}/attachments/${attachmentId}`);
  }

  unlinkAttachment(ticketId, attachmentId) {
    return axios.delete(`${this.url}/${ticketId}/attachments/${attachmentId}`);
  }

  uploadAttachment(ticketId, file) {
    const formData = new FormData();
    formData.append('attachment', file);
    return axios.post(`${this.url}/${ticketId}/upload_attachment`, formData);
  }
}

export default new TicketsAPI();
